import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClassItem } from '@/data/classes';
import {
	getOpenClassListCoursePeriodLine,
	inferOpenClassListParticipationMode,
} from '@/utils/classUtils';

/**
 * 오픈클래스 목록·랜딩 카드 — 참여방법 / 수강기간 (일자가 있으면 온라인·대면 동일 한글 범위, 숫자만 오면 N일)
 */
export function OpenClassListCardMeta({ item }: { item: ClassItem }): React.ReactElement {
	const { t, i18n } = useTranslation('class');
	const mode = useMemo(() => inferOpenClassListParticipationMode(item), [item]);

	const methodText = useMemo(() => {
		if (mode === 'online') return t('class:onlineParticipation');
		if (mode === 'offline') return t('class:inPersonParticipation');
		return t('class:openListParticipationUnknown');
	}, [mode, t]);

	const periodText = useMemo(() => {
		const periodRaw = getOpenClassListCoursePeriodLine(item, mode, i18n.language);
		if (!periodRaw?.trim()) return '—';
		const looksLikeDayCount = /^\d+$/.test(periodRaw.trim());
		if (looksLikeDayCount) {
			const n = parseInt(periodRaw, 10);
			if (!Number.isFinite(n) || n < 1) return '—';
			return t('class:openListDays', { count: n });
		}
		return periodRaw;
	}, [i18n.language, item, mode, t]);

	return (
		<div className="mb-3 space-y-2.5 min-h-[3.25rem]">
			<div>
				<span className="text-gray-500 text-xs block mb-0.5">{t('class:openListParticipationMethod')}</span>
				<span className="text-gray-800 text-sm font-medium leading-snug">{methodText}</span>
			</div>
			<div>
				<span className="text-gray-500 text-xs block mb-0.5">{t('class:openListCoursePeriod')}</span>
				<span className="text-gray-800 text-sm font-medium leading-snug break-words">{periodText}</span>
			</div>
		</div>
	);
}
